/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import * as ApiService from './ApiService';

const baseUrl = 'properties';

/**
 * motili property autosuggest api
 * @param {string} value
 * @param {boolean} [existingOnly]
 * @param {object} [additionalFilter]
 * @param {number} [minPrefixLength]
 * @param {boolean} activeOnly
 * @returns {Promise<Array>} response
 */
export function suggest(
    value,
    existingOnly,
    additionalFilter,
    minPrefixLength,
    activeOnly = false
) {
    return ApiService.get(
        true,
        `${baseUrl}/suggest`,
        {
            prefix: value,
            minPrefixLength,
            limit: undefined,
            existingOnly,
            activeOnly,
            additionalFilter,
        },
        true
    );
}

/**
 * motili property autosuggest api, returns first result
 * if returnExistsOnly argument is true, will only return first result if isKnown and has an id
 *
 * @param {string} value
 * @param {boolean} [existingOnly]
 * @param {object} [additionalFilter]
 * @returns {Promise<Object>} response
 */
export async function suggestOne(value, existingOnly, additionalFilter) {
    const suggestions = await suggest(value, existingOnly, additionalFilter);
    if (suggestions.length === 0) {
        return null;
    }
    return suggestions[0];
}

/**
 *
 * @param {number} id
 * @param {object} [query]
 * @returns {Promise<Object>}
 */
export function findById(id, query) {
    return ApiService.get(true, `${baseUrl}/${id}`, query);
}

/**
 *
 * @param {number} id
 * @param {object} [query]
 * @returns {Promise<Object>}
 */
export function findByIdWithPreferredContractor(id, query) {
    return ApiService.get(
        true,
        `${baseUrl}/findByIdWithPreferredContractor/${id}`,
        query
    );
}

/**
 *
 * @param {object} [data]
 * @returns {Promise<Object>}
 */
export function find(data) {
    return ApiService.post(true, `${baseUrl}/search`, data);
}

/**
 * Create a new note for the property
 *
 * @param {*} propertyId
 * @param {*} data
 *
 * @returns {*}
 */
export function createNote(propertyId, data) {
    return ApiService.post(true, `${baseUrl}/${propertyId}/notes`, data);
}

/**
 * Update a note for the property
 * @param {*} propertyId
 * @param {*} noteId
 * @param {*} data
 *
 * @returns {*}
 */
export function updateNote(propertyId, noteId, data) {
    return ApiService.put(
        true,
        `${baseUrl}/${propertyId}/notes/${noteId}`,
        data
    );
}

/**
 * Get properties ids that stdAddress contains address
 * @param {*} address
 *
 * @returns {Promise<{id: number}[]>}
 */
export function getPropertiesByAddress(address) {
    const filter = {
        fields: ['id'],
        where: {
            stdAddress: {
                ilike: `%${address}%`,
            },
        },
    };
    return ApiService.get(true, `${baseUrl}`, { filter });
}

export function getPropertyProfile(propertyId, propertyProfileId, query) {
    return ApiService.get(
        true,
        `${baseUrl}/${propertyId}/propertyProfile/${propertyProfileId}`,
        query
    );
}

/**
 * Get only parent properties
 * @param {object} query
 * @return {Promise}
 */
export function getParentProperties(query) {
    return ApiService.get(true, `${baseUrl}/parents`, query);
}

export function getPropertiesByParent(filter) {
    return ApiService.get(true, `${baseUrl}/propertiesByParent`, filter);
}

export function findRecentJobs(propertyIds) {
    return ApiService.get(true, `${baseUrl}/recentJobs`, {
        propertyIds,
    });
}

export function countTotalChildPropertyByParent(parentPropertyId, filter) {
    return ApiService.get(
        true,
        `${baseUrl}/countPropertiesByParent/${parentPropertyId}`,
        filter
    );
}

export function getPropertyOrderHistory(propertyId) {
    return ApiService.get(true, `${baseUrl}/${propertyId}/orderHistory`);
}
